/**
 * Step 1 — Dados do Titular.
 *
 * Os dados pessoais chegam prontos do `resolve` e ficam bloqueados: o chip verde
 * "Pré-preenchido" comunica que não há nada a fazer ali. Só o contato pessoal é
 * editável, e o chip âmbar "Atualização necessária" marca a seção que exige ação
 * — são os dois campos que vão no submit como `confirmed_email` e
 * `confirmed_phone`.
 */

import { TextField } from "@mui/material";

import { cpfMask } from "utils/cpf";

import { StatusChip } from "../components/StatusChip";
import { StepActions } from "../components/StepActions";
import { useExtensionForm } from "../context";
import { formatIsoDate, formatPhone } from "../helpers";
import {
  Header,
  Panel,
  Paragraph,
  Row,
  SectionLabel,
  Stack,
  TwoColumns,
} from "../styled";

const PHONE_MIN_DIGITS = 10;

const PHONE_MAX_DIGITS = 11;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function Step1Holder() {
  const { data, state, updateState, goNext, goBack } = useExtensionForm();
  const { holder } = data;

  const email = state.contact.personalEmail;
  const phone = state.contact.mobilePhone;

  const hasEmail = email.trim().length > 0;
  const isEmailValid = EMAIL_PATTERN.test(email.trim());
  const isPhoneValid = phone.replace(/\D/g, "").length >= PHONE_MIN_DIGITS;

  const canProceed = isEmailValid && isPhoneValid;

  function updateContact(patch: Partial<typeof state.contact>) {
    updateState({ contact: { ...state.contact, ...patch } });
  }

  return (
    <Stack $gap="24px">
      <Stack $gap="4px">
        <Header>Dados do Titular</Header>
        <Paragraph>
          Seu e-mail e telefone corporativos não funcionarão mais. Informe seus
          dados pessoais para receber o boleto.
        </Paragraph>
      </Stack>

      <Panel>
        <Stack $gap="16px">
          <Row $justify="space-between">
            <SectionLabel>Dados pessoais</SectionLabel>
            <StatusChip tone="success">Pré-preenchido</StatusChip>
          </Row>

          <TwoColumns $ratio="2fr 1fr 1fr">
            <TextField
              label="Nome"
              value={holder.name}
              disabled
            />
            <TextField
              label="CPF"
              value={cpfMask(holder.taxPayerId)}
              disabled
            />
            <TextField
              label="Data de nascimento"
              value={formatIsoDate(holder.birthDate)}
              disabled
            />
          </TwoColumns>
        </Stack>
      </Panel>

      <Panel>
        <Stack $gap="16px">
          <Row $justify="space-between">
            <SectionLabel>Contato pessoal</SectionLabel>
            <StatusChip tone="warning">Atualização necessária</StatusChip>
          </Row>

          <TwoColumns>
            <TextField
              label="E-mail pessoal"
              placeholder="seuemail@exemplo.com"
              value={email}
              onChange={(event) =>
                updateContact({ personalEmail: event.target.value })
              }
              error={hasEmail && !isEmailValid}
              helperText={
                hasEmail && !isEmailValid ? "E-mail inválido." : undefined
              }
            />
            <TextField
              label="Telefone celular"
              placeholder="(00) 00000-0000"
              value={formatPhone(phone)}
              onChange={(event) =>
                updateContact({
                  mobilePhone: event.target.value
                    .replace(/\D/g, "")
                    .substring(0, PHONE_MAX_DIGITS),
                })
              }
            />
          </TwoColumns>
        </Stack>
      </Panel>

      <StepActions
        nextLabel="Próximo: Plano →"
        onNext={goNext}
        onBack={goBack}
        isNextDisabled={!canProceed}
      />
    </Stack>
  );
}
